import type { FlexProps } from '@chakra-ui/react';
import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { parseUnits } from '@ethersproject/units';
import Info from '@ui/components/Info/Info';
import { InfoTooltip } from '@ui/components/InfoTooltip';
import useDebounce from '@ui/hooks/useDebounce';
import useLpUsdValue from '@ui/hooks/useLpUsdValue';
import { useStrategyPriceImpact } from '@ui/hooks/useStrategyPriceImpact';
import { useTranslations } from '@ui/i18n';
import bigNumberToNumber from '@ui/utils/bigNumberToNumber';
import {
  formatNumber,
  formatPercentage,
  formatTokenAmount,
} from '@ui/utils/format';
import type { GardenToken } from '@ui/utils/garden';
import { gardenTokenDict } from '@ui/utils/garden';
import type { LiquidityStrategyDepositToken } from '@ui/utils/strategy';
import { LiquidityStrategyTokenDict } from '@ui/utils/strategy';
import { InFerro } from '@veno-app/chakra';
import { Fragment } from 'react';

import { useStrategyFlowDrawer } from './StrategyFlowDrawer';
import StrategySlippage from './StrategySlippage';
import { useReceiveInfoForToken } from './useReceiveTokenInfo';

export const ReceiveRows = ({
  isWithdraw,
  amountStr,
  token,
  showDetails,
  ...props
}: FlexProps & {
  isWithdraw: boolean;
  amountStr: string;
  token: LiquidityStrategyDepositToken;
  showDetails?: boolean;
}) => {
  const t = useTranslations();

  const debouncedAmountStr = useDebounce(amountStr, 500);
  const debouncedAmount = parseUnits(debouncedAmountStr || '0');
  const tokenInfo = LiquidityStrategyTokenDict[token];
  const priceImpact = useStrategyPriceImpact(
    token,
    debouncedAmount,
    isWithdraw,
  ).data;

  const {
    receivedAmount,
    receivedAmountUsd,
    exchangeRate,
    ferroLpAmountTransferred,
  } = useReceiveInfoForToken(token, debouncedAmount, isWithdraw);

  const croFerroGardenInfo = gardenTokenDict.cro.FERRO as Extract<
    GardenToken,
    {
      enabled: true;
    }
  >;
  const ferroLpAmountTransferredUsd = useLpUsdValue(
    'FERRO',
    ferroLpAmountTransferred
      ? String(
          bigNumberToNumber(
            ferroLpAmountTransferred,
            croFerroGardenInfo.decimals,
          ),
        )
      : '0',
  );

  return (
    <Box {...props}>
      <Info
        minH="50px"
        title={
          <>
            {t('You will receive')}{' '}
            {!isWithdraw && (
              <InfoTooltip
                title={tokenInfo.vaultTokenName}
                label={
                  <Text
                    px={{ base: 0, desktop: 8 }}
                    py={{ base: 0, desktop: 4 }}
                  >
                    {t('vlcro-explainer-{token}-{strategy}', {
                      token: tokenInfo.vaultTokenName,
                      strategy: t('Liquidity strategy'),
                    })}
                  </Text>
                }
              />
            )}
          </>
        }
        convertedValueLoading={!receivedAmountUsd && !!amountStr}
        convertedValue={
          receivedAmountUsd
            ? formatTokenAmount(receivedAmountUsd, 2) + ' USD'
            : undefined
        }
        value={
          isWithdraw ? (
            receivedAmount && !Number.isNaN(receivedAmount) ? (
              <Flex alignItems="center">
                <Text>{receivedAmount}</Text>
                <tokenInfo.Icon />
              </Flex>
            ) : amountStr ? undefined : (
              '--'
            )
          ) : receivedAmount && !Number.isNaN(receivedAmount) ? (
            `${receivedAmount} ${tokenInfo.vaultTokenName}`
          ) : amountStr ? undefined : (
            '--'
          )
        }
        mt="16px"
      />
      <Info
        title={t('Exchange rate')}
        value={`1 ${tokenInfo.shortName} : ${
          exchangeRate ? (1 / exchangeRate).toFixed(4) : '--'
        } ${tokenInfo.vaultTokenName}`}
        mt="16px"
      />
      <Info
        hidden={!showDetails}
        title={isWithdraw ? t('Withdrawal Fee') : t('Deposit Fee')}
        value={formatPercentage(0)}
        mt="16px"
      />
      <Button
        hidden={!showDetails}
        variant="link"
        display="block"
        color="text-link"
        mt="16px"
        mx="auto"
        onClick={() => {
          useStrategyFlowDrawer.getState().onOpen({
            isWithdraw,
            token,
          });
        }}
      >
        {t('What happens to my {coin}?', {
          coin: tokenInfo.name,
        })}
      </Button>
      <Box
        hidden={!showDetails || token === 'ferroLP'}
        bgColor="brand-black"
        bgImage="/images/strategy-ferro-bg.png"
        bgSize="100% 100%"
        rounded="10px"
        p="12px 16px"
        mt="16px"
      >
        <InFerro>
          <Info
            minH="50px"
            title={
              isWithdraw
                ? t('{coin} burned', {
                    coin: croFerroGardenInfo.name,
                  })
                : t('{coin} minted', {
                    coin: croFerroGardenInfo.name,
                  })
            }
            value={
              ferroLpAmountTransferred
                ? formatTokenAmount(
                    ferroLpAmountTransferred,
                    2,
                    gardenTokenDict.cro.FERRO.decimals,
                  )
                : '--'
            }
            convertedValueLoading={
              typeof ferroLpAmountTransferredUsd === 'undefined'
            }
            convertedValue={
              typeof ferroLpAmountTransferredUsd !== 'undefined'
                ? formatNumber(ferroLpAmountTransferredUsd, 2, true) + ' USD'
                : undefined
            }
          />
          <Info
            color={priceImpact && priceImpact < -0.05 ? 'red.500' : undefined}
            title={
              (priceImpact ?? 0) > 0
                ? t('Bonus')
                : t('Price impact') + ' (Ferro)'
            }
            value={
              priceImpact !== undefined
                ? `${formatPercentage(priceImpact, 4)}`
                : undefined
            }
            mt="16px"
          />
          <StrategySlippage />
        </InFerro>
      </Box>
    </Box>
  );
};
